'use client';

import DashboardSidebar from '@/components/shared/layouts/DashboardSidebar';
import { LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from 'next-intl';
import { memo, Suspense, useEffect, useRef, useState } from 'react';

/**
 * يحصل على نوع المستخدم من localStorage فقط (بدون query parameter)
 * هذا يضمن أن Sidebar لا يتغير عند التنقل بين الصفحات
 */
function getStoredJourneyUserType(): 'client' | 'supplier' | null {
  if (typeof window === 'undefined') return null;

  const storedUserType = localStorage.getItem('__journey_user_type__');
  if (storedUserType === 'client' || storedUserType === 'supplier') {
    return storedUserType;
  }

  return null;
}

const SIDEBAR_USER_TYPE_STORAGE_KEY = '__sidebar_user_type__';

function getStoredSidebarUserType(): 'client' | 'supplier' | null {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem('token');
  if (!token) return null;
  const storedUserType = localStorage.getItem(SIDEBAR_USER_TYPE_STORAGE_KEY);
  if (storedUserType === 'client' || storedUserType === 'supplier') {
    return storedUserType;
  }
  return null;
}

function MainLayout({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const { user, isLoading } = useAuth();

  // حفظ نوع المستخدم في state - نبدأ دائماً بـ null لتجنب hydration mismatch
  // (السيرفر لا يملك localStorage، العميل قد يملك قيمة مخزنة)
  const [sidebarUserType, setSidebarUserType] = useState<'client' | 'supplier' | null>(null);
  const isInitialized = useRef(false);
  const isInJourneyMode = useRef(false);

  // التهيئة الأولية - مرة واحدة فقط
  useEffect(() => {
    if (isInitialized.current) return;

    // أولاً: التحقق من localStorage (journey mode)
    const storedJourneyType = getStoredJourneyUserType();
    if (storedJourneyType) {
      setSidebarUserType(storedJourneyType);
      isInJourneyMode.current = true;
      isInitialized.current = true;
      return;
    }

    // ثانياً: استخدام user type من auth (normal mode)
    if (user?.userType === 'client' || user?.userType === 'supplier') {
      setSidebarUserType(user.userType);
      localStorage.setItem(SIDEBAR_USER_TYPE_STORAGE_KEY, user.userType);
      isInJourneyMode.current = false;
      isInitialized.current = true;
      return;
    }

    // لا يوجد نوع مستخدم - لا تظهر Sidebar
    isInitialized.current = true;
  }, [user?.userType]);

  // مراقبة تغييرات journey type من UserJourneyTester فقط
  useEffect(() => {
    const handleJourneyTypeChange = (e: CustomEvent<{ userType: string | null }>) => {
      if (!isInitialized.current) return;

      const newUserType = e.detail.userType;

      if (newUserType === 'client' || newUserType === 'supplier') {
        // تم تغيير journey type بشكل صريح - تحديث Sidebar
        setSidebarUserType(newUserType);
        isInJourneyMode.current = true;
      } else if (newUserType === null) {
        // تم إزالة journey type - العودة إلى user type من auth
        if (user?.userType === 'client' || user?.userType === 'supplier') {
          setSidebarUserType(user.userType);
        } else {
          setSidebarUserType(null);
        }
        isInJourneyMode.current = false;
      }
    };

    window.addEventListener('journeyUserTypeChanged', handleJourneyTypeChange as EventListener);

    return () => {
      window.removeEventListener(
        'journeyUserTypeChanged',
        handleJourneyTypeChange as EventListener
      );
    };
  }, [user?.userType]);

  // تحديث Sidebar عند تغيير user من auth (فقط في الوضع العادي)
  useEffect(() => {
    if (!isInitialized.current) return;
    if (isInJourneyMode.current) return; // لا نغير في journey mode

    if (user?.userType === 'client' || user?.userType === 'supplier') {
      setSidebarUserType(user.userType);
      localStorage.setItem(SIDEBAR_USER_TYPE_STORAGE_KEY, user.userType);
    } else {
      if (!isLoading) {
        setSidebarUserType(null);
        localStorage.removeItem(SIDEBAR_USER_TYPE_STORAGE_KEY);
      }
    }
  }, [user?.userType, isLoading]);

  // إظهار Sidebar فقط للمستخدمين المسجلين (client/supplier)
  const hasSidebar = sidebarUserType === 'client' || sidebarUserType === 'supplier';

  return (
    <div className="flex min-h-screen">
      {hasSidebar && sidebarUserType && <DashboardSidebar userType={sidebarUserType} />}
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${hasSidebar ? (isRTL ? 'lg:mr-[var(--sidebar-width,280px)]' : 'lg:ml-[var(--sidebar-width,280px)]') : ''} pb-20 lg:pb-0`}
      >
        <Suspense
          fallback={
            <div className="flex h-64 items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          {children}
        </Suspense>
      </main>
    </div>
  );
}

// Memoize the layout - children will change on navigation, but layout structure stays the same
export default memo(MainLayout);
